import { yupResolver } from '@hookform/resolvers/yup';
import queryString from 'query-string';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { GENERAL_ERROR_MESSAGE, LINK_COPIED } from 'constants/texts';
import { showErrorSnackbar, showSuccessSnackbar } from 'hooks/useSnackbar';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { IGuestPortal } from 'utils/itineraryDetails/types';

import { typesValidationSchema } from './validation';

type FieldValues = {
  [key: string]: boolean;
};

export const useSyncCalendarForm = () => {
  const itinerary = useSelector(itinerariesItemDataSelector) as IGuestPortal;
  const eventTypes = itinerary?.tripEventTypes || [];
  const baseLink = itinerary?.calendarSyncLink || '';
  const isV2 = itinerary.detailsMode === 'chronological';

  const methods = useForm<FieldValues>({
    defaultValues: Object.fromEntries(eventTypes.map((type) => [type, true])),
    resolver: isV2 ? yupResolver(typesValidationSchema) : undefined,
  });

  const { handleSubmit } = methods;

  const generateCalendarLink = (types: string[]) => {
    const queryParams = queryString.stringify({ types }, { arrayFormat: 'bracket' });
    return queryParams ? `${baseLink}?${queryParams}` : baseLink;
  };

  const getCalendarLink = (fieldValues: FieldValues) => {
    const selectedTypes = Object.keys(fieldValues).filter((type) => fieldValues[type]);
    const calendarLink = generateCalendarLink(selectedTypes);
    return calendarLink;
  };

  const handleCopy = handleSubmit(async (fieldValues) => {
    try {
      await navigator.clipboard.writeText(getCalendarLink(fieldValues));
      showSuccessSnackbar(LINK_COPIED);
    } catch {
      showErrorSnackbar(GENERAL_ERROR_MESSAGE);
    }
  });

  return { methods, eventTypes, handleCopy, getCalendarLink };
};
